import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPackagesForSimulator, simulateBasket, addPackageToCart } from './api';
import { useCart } from '@/features/cart/context/CartContext';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getPackageDisplayName, getProductDisplayName } from '@/lib/packageDisplay';
import { ArrowLeft, ArrowRight, Package, Store } from 'lucide-react';
import './basket-simulator.css';

const fmt = (n) => new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtPercent = (n) => `${new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 1 }).format(Number(n) || 0)}٪`;

function PackageImage({ src, alt, kind }) {
    const [hasError, setHasError] = useState(!src);

    if (!src || hasError) {
        const FallbackIcon = kind === 'vendor' ? Store : Package;
        return <FallbackIcon className="package-card-image-fallback" size={kind === 'vendor' ? 18 : 20} aria-hidden="true" />;
    }

    return (
        <img
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            onError={() => setHasError(true)}
        />
    );
}

function PackageCard({ pkg, onSelect }) {
    const products = Array.isArray(pkg.products) ? pkg.products : [];
    const packageName = getPackageDisplayName(pkg.package_name);
    const vendorName = pkg.vendor_name || 'فروشگاه بدون نام';
    const isSoldOut = Boolean(pkg.is_sold_out);

    return (
        <button
            type="button"
            className={`package-card${isSoldOut ? ' is-sold-out' : ''}`}
            onClick={() => onSelect(pkg)}
            disabled={isSoldOut}
            aria-label={isSoldOut
                ? `${packageName} از فروشگاه ${vendorName}؛ تکمیل ظرفیت`
                : `مشاهده و انتخاب ${packageName} از فروشگاه ${vendorName}`}
        >
            <span className="package-card-vendor">
                <span className="package-card-vendor-logo">
                    <PackageImage src={pkg.vendor_logo} alt={vendorName} kind="vendor" />
                </span>
                <span className="package-card-vendor-name">
                    از فروشگاه: <strong>{vendorName}</strong>
                </span>
            </span>

            <span className="package-card-title" title={packageName} role="heading" aria-level="2">
                {packageName}
            </span>

            <span className="package-card-meta">
                <span className="package-card-badge">
                    <Package size={14} aria-hidden="true" />
                    {fmt(pkg.product_count)} محصول
                </span>
                {isSoldOut && <span className="package-card-badge package-card-sold-out">تکمیل ظرفیت</span>}
            </span>

            <span className="package-card-thumbnails" aria-hidden="true">
                {products.slice(0, 3).map((product, index) => (
                    <span className="package-card-thumbnail" key={product.id || index}>
                        <PackageImage src={product.image_url} alt="" kind="product" />
                    </span>
                ))}
            </span>

            <span className="package-card-products">
                <span className="package-card-products-heading">اقلام این پکیج</span>
                <span className="package-card-product-list" role="list" aria-label="اقلام پکیج">
                    {products.length > 0 ? products.slice(0, 3).map((product, index) => {
                        const productName = getProductDisplayName(product.name);
                        return (
                            <span
                                className="package-card-product-name"
                                key={product.id || index}
                                title={productName}
                                role="listitem"
                            >
                                {productName}
                            </span>
                        );
                    }) : (
                        <span className="package-card-product-empty" role="listitem">اقلام این پکیج ثبت نشده است</span>
                    )}
                </span>
                <span className="package-card-more">
                    {products.length > 3 ? `+${fmt(products.length - 3)} مورد دیگر` : '\u00a0'}
                </span>
            </span>

            {!isSoldOut && (
                <span className="package-card-cta">
                    <span>مشاهده و انتخاب پکیج</span>
                    <ArrowLeft size={16} aria-hidden="true" />
                </span>
            )}
        </button>
    );
}

export default function BasketSimulatorPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { refreshCart } = useCart();
    const { user, activeRole, loading: authLoading } = useAuth();

    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [calculating, setCalculating] = useState(false);
    const [addingToCart, setAddingToCart] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const data = await getPackagesForSimulator();
                setPackages(data);
                setSelectedPackage(null);
                setResult(null);
            } catch (e) { setError(e.message); } finally { setLoading(false); }
        })();
    }, []);

    const selectPackage = (pkg) => {
        setSelectedPackage(pkg); setResult(null); setError('');
    };

    const backToPackages = () => {
        setSelectedPackage(null); setResult(null); setError('');
    };

    const buildItems = () => {
        if (!selectedPackage) return [];
        return selectedPackage.products.map(p => ({
            product_id: p.id,
            quantity: p.min_group_quantity || 1
        }));
    };

    const handleSimulate = async () => {
        if (!selectedPackage) return;
        const items = buildItems();

        if (items.length === 0) { setError('این پکیج هیچ محصولی ندارد.'); return; }

        setCalculating(true); setError('');
        try {
            const data = await simulateBasket({
                package_id: selectedPackage.package_id
            });
            setResult(data);
        } catch (e) { setError(e.message); } finally { setCalculating(false); }
    };

    const handleAddToCart = async () => {
        if (!result || !selectedPackage) return;
        if (authLoading) {
            setError('لطفاً چند لحظه صبر کنید تا وضعیت حساب کاربری بررسی شود.');
            return;
        }
        if (!user) {
            navigate('/login', {
                state: {
                    from: location,
                    message: 'برای افزودن کالا به سبد خرید، ابتدا وارد حساب کاربری شوید.',
                },
            });
            return;
        }
        if (activeRole !== 'customer') {
            setError('برای افزودن کالا به سبد خرید، با حساب مشتری وارد شوید.');
            return;
        }

        const items = buildItems();
        setAddingToCart(true); setError('');
        try {
            await addPackageToCart({
                vendor_id: selectedPackage.vendor_id,
                package_id: selectedPackage.package_id,
                items
            });
            if (refreshCart) await refreshCart();
            navigate('/cart');
        } catch (e) { setError(e.message); } finally { setAddingToCart(false); }
    };

    const handleCancel = () => {
        setResult(null); setError('');
    };

    if (!selectedPackage) {
        return (
            <div className="basket-simulator-page basket-simulator-list">
                <header className="basket-sim-list-header">
                    <h1>انتخاب کالاها و استفاده از تخفیف خرید گروهی</h1>
                    <p>پکیج موردنظرتان را انتخاب کنید تا اقلام و تخفیف آن را بررسی کنید.</p>
                </header>
                {error && <div className="basket-sim-error" role="alert">{error}</div>}
                {loading && (
                    <div className="package-grid package-grid-loading" role="status" aria-label="در حال بارگذاری پکیج‌ها" aria-busy="true">
                        {Array.from({ length: 6 }, (_, index) => (
                            <div className="package-card-skeleton" key={index} aria-hidden="true">
                                <span className="package-skeleton-vendor" />
                                <span className="package-skeleton-title" />
                                <span className="package-skeleton-meta" />
                                <span className="package-skeleton-images" />
                                <span className="package-skeleton-products" />
                                <span className="package-skeleton-cta" />
                            </div>
                        ))}
                    </div>
                )}
                {!loading && packages.length === 0 && !error && (
                    <div className="basket-sim-empty" role="status">
                        <Package size={28} aria-hidden="true" />
                        <strong>در حال حاضر پکیجی برای خرید گروهی در دسترس نیست.</strong>
                        <span>بعداً دوباره سر بزنید؛ پکیج‌های فعال اینجا نمایش داده می‌شوند.</span>
                    </div>
                )}
                {!loading && packages.length > 0 && (
                    <div className="package-grid">
                        {packages.map((pkg) => (
                            <PackageCard key={pkg.package_id} pkg={pkg} onSelect={selectPackage} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="basket-simulator-page basket-simulator-detail" style={{ maxWidth: '1400px', margin: '0 auto', padding: '1rem', display: 'flex', flexDirection: 'column' }}>

            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '0.75rem 1.5rem', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ margin: '0 0 0.2rem 0', fontSize: '1.3rem', color: '#1e293b' }}>{getPackageDisplayName(selectedPackage.package_name)}</h1>
                    <p style={{ margin: 0, color: '#475569', fontSize: '0.85rem' }}>از فروشگاه: <strong>{selectedPackage.vendor_name}</strong></p>
                </div>
                <button
                    type="button"
                    onClick={backToPackages}
                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '6px 12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', transition: 'all 0.2s' }}
                >
                    <ArrowRight size={16} /> بازگشت به لیست سبدها
                </button>
            </header>

            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</div>}

            <div className="basket-sim-detail-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(350px, 550px) 1fr',
                gap: '1.5rem',
                flex: 1,
                minHeight: 0
            }}>

                {/* ستون راست: محصولات پکیج (با اسکرول داخلی) */}
                <div className="basket-sim-panel" style={{ background: '#fff', padding: '1rem', borderRadius: '12px', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '0.75rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
                        <Package size={18} className="text-blue-500" /> اقلام موجود در این پکیج
                    </h3>

                    <div className="basket-sim-product-list" style={{ overflowY: 'auto', paddingRight: '0.5rem', flex: 1 }}>
                        {selectedPackage.products.map((p) => {
                            const fixedQty = p.min_group_quantity || 1;

                            return (
                                <div className="basket-sim-product-row" key={p.id} style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem', border: '1px solid #10b981', borderRadius: '8px', marginBottom: '0.75rem', background: '#f0fdf4', transition: 'all 0.2s', alignItems: 'center' }}>
                                    <div style={{ flexShrink: 0 }}>
                                        {p.image_url ? (
                                            <img src={p.image_url} alt={p.name} style={{ width: '50px', height: '50px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                                        ) : (
                                            <div style={{ width: '50px', height: '50px', borderRadius: '8px', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#64748b' }}>بدون عکس</div>
                                        )}
                                    </div>

                                    <div className="basket-sim-product-info" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <strong style={{ fontSize: '0.9rem', color: '#111' }}>{p.name}</strong>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.2rem' }}>
                                            <div style={{ color: '#059669', fontWeight: 'bold', fontSize: '0.85rem' }}>
                                                {fmt(p.consumer_price)} ریال
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '80px' }}>
                                        <div style={{ background: '#d1fae5', color: '#065f46', padding: '4px 10px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.85rem', border: '1px solid #a7f3d0' }}>
                                            {fmt(fixedQty)} عدد
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ستون چپ: دکمه شبیه‌ساز و نتایج (Sidebar) */}
                <div className="basket-sim-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%', overflowY: 'auto' }}>
                    <button
                        onClick={handleSimulate}
                        disabled={calculating}
                        style={{
                            width: '100%',
                            padding: '0.8rem',
                            fontSize: '1.05rem',
                            fontWeight: 'bold',
                            color: '#fff',
                            background: calculating ? '#94a3b8' : '#3b82f6',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: calculating ? 'not-allowed' : 'pointer',
                            boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                            transition: 'all 0.2s',
                            flexShrink: 0
                        }}
                    >
                        {calculating ? 'در حال بررسی هوشمند...' : 'بررسی سبد و محاسبه تخفیف'}
                    </button>

                    {result && (
                        <div className="result-panel" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '0.75rem', flexShrink: 0 }}>
                            {result.is_safe ? (
                                <div className="status-card status-success" style={{ textAlign: 'center', padding: '0' }}>

                                    <h2 style={{ fontSize: '1rem', color: '#065f46', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                                        <span>📢</span> میزان سود شما
                                    </h2>

                                    <p className="basket-sim-referral-promo" style={{ margin: '0 0 0.5rem 0', fontSize: '0.75rem', color: '#047857', background: '#ecfdf5', padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px dashed #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                                        <span>🎁</span> پس از خرید، با کد معرف خود دوستانتان را دعوت کرده و هدیه بگیرید.
                                    </p>

                                    <div className="price-breakdown" style={{ marginTop: '0.2rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.2rem', marginBottom: 0, paddingBottom: 0 }}>
                                        <div className="price-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem', fontSize: '0.85rem' }}><span>قیمت اصلی سبد:</span><span className="strike-through" style={{ textDecoration: 'line-through', color: '#94a3b8' }}>{fmt(result.original_total_price)} ریال</span></div>
                                        {result.discount_amount > 0 && (
                                            /* === [CHANGED] تغییر جهت به LTR برای قرارگیری منفی در سمت چپ === */
                                            <div className="price-row discount-row" style={{ display: 'flex', justifyContent: 'space-between', color: '#e11d48', marginBottom: '0.2rem', fontSize: '0.85rem' }}>
                                                <span>تخفیف ویژه ({fmtPercent(result.discount_percent)}):</span>
                                                <span style={{ direction: 'ltr' }}>- {fmt(result.discount_amount)} ریال</span>
                                            </div>
                                            /* =============================================================== */
                                        )}
                                        <div className="price-row final-row" style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.1rem', color: '#2563eb', marginTop: '0.2rem', paddingTop: '0.2rem', marginBottom: 0 }}>
                                            <span>قابل پرداخت:</span><strong>{fmt(result.final_payable_price)} ریال</strong>
                                        </div>
                                    </div>

                                    <div className="result-actions" style={{ display: 'flex', flexDirection: 'row', gap: '0.75rem', marginTop: '0.75rem', marginBottom: 0 }}>
                                        <button
                                            onClick={handleCancel}
                                            style={{
                                                flex: 1,
                                                padding: '0.5rem',
                                                background: '#f1f5f9',
                                                color: '#475569',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            انصراف
                                        </button>
                                        <button
                                            onClick={handleAddToCart}
                                            disabled={addingToCart || authLoading}
                                            style={{
                                                flex: 2.5,
                                                padding: '0.5rem',
                                                background: '#10b981',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontWeight: 'bold',
                                                fontSize: '0.95rem',
                                                cursor: addingToCart || authLoading ? 'not-allowed' : 'pointer',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {addingToCart ? 'در حال افزودن...' : authLoading ? 'در حال بررسی حساب...' : 'افزودن به سبد خرید'}
                                        </button>
                                    </div>

                                </div>
                            ) : (
                                <div className="status-card status-danger" style={{ textAlign: 'center', padding: 0 }}>
                                    <div className="status-icon" style={{ fontSize: '2rem' }}>⚠️</div>
                                    <h2 style={{ fontSize: '1rem', color: '#991b1b', margin: '0.5rem 0' }}>{result.message}</h2>
                                    <p style={{ fontSize: '0.85rem', color: '#7f1d1d', background: '#fef2f2', padding: '0.75rem', borderRadius: '6px', marginTop: '0.5rem', lineHeight: '1.5', marginBottom: 0 }}>{result.advice}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}